import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  Eye,
  Edit,
  Trash2,
  FileWarning
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownItem
} from './style';

interface ActionsDropdownProps {
  buttonRef: HTMLButtonElement | null;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onGenerateRNC?: () => void;
  onClose?: () => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ 
  buttonRef,
  onView, 
  onEdit, 
  onDelete,
  onGenerateRNC,
  onClose 
}) => {
  const [dropdownPosition, setDropdownPosition] = useState<{ 
    top: number; 
    left: number; 
    width: number; 
    direction: 'up' | 'down' 
  }>({ top: 0, left: 0, width: 0, direction: 'down' });
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        if (buttonRef && !buttonRef.contains(target)) {
          if (onClose) onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, buttonRef]);

  useEffect(() => {
    const handleScrollOrResize = () => {
      if (onClose) onClose();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [onClose]);

  useEffect(() => {
    if (!buttonRef) return;

    const buttonRect = buttonRef.getBoundingClientRect();
    const menuHeight = 200;
    const menuWidth = 200;
    const windowHeight = window.innerHeight;
    
    const buttonCenterX = buttonRect.left + (buttonRect.width / 2);
    const left = buttonCenterX - (menuWidth / 2);
    
    let direction: 'up' | 'down' = 'down';
    let top = buttonRect.bottom + 4;
    
    const spaceBelow = windowHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      direction = 'up';
      top = buttonRect.top - menuHeight - 4;
    } else {
      direction = 'down';
      top = buttonRect.bottom + 4;
    }
    
    setDropdownPosition({
      top: Math.round(top),
      left: Math.round(left),
      width: menuWidth,
      direction
    });
  }, [buttonRef]);

  const handleAction = (action: () => void) => {
    action();
    if (onClose) onClose();
  };

  return ReactDOM.createPortal(
    <DropdownMenu
      ref={dropdownRef}
      top={dropdownPosition.top}
      left={dropdownPosition.left}
      width={dropdownPosition.width}
      direction={dropdownPosition.direction}
    >
      {onView && (
        <DropdownItem onClick={() => handleAction(onView)}>
          <Eye size={16} />
          Visualizar
        </DropdownItem>
      )}
      {onEdit && (
        <DropdownItem onClick={() => handleAction(onEdit)}>
          <Edit size={16} />
          Editar
        </DropdownItem>
      )}
      {onGenerateRNC && (
        <DropdownItem onClick={() => handleAction(onGenerateRNC)}>
          <FileWarning size={16} />
          Gerar RNC
        </DropdownItem>
      )}
      {onDelete && (
        <DropdownItem onClick={() => handleAction(onDelete)}>
          <Trash2 size={16} />
          Excluir
        </DropdownItem>
      )}
    </DropdownMenu>,
    document.body
  );
};

export default ActionsDropdown;